import { ReactNode, useCallback, useEffect, useState } from 'react'

import { useTheme } from '@emotion/react'
import { useTranslation } from 'react-i18next'

import { DeviceMobileIcon } from '@arcade/web/icons/react'
import { ArcadeButton, Flex, spacing, Stack, Text, Toast } from '@cash-design-system/react'
import { TriggerCampaignRequestId } from '@cash-web/protos/squareup/cashfrontend/app.pb'
import { useMutationTriggerCampaign } from '@cash-web/shared-data-access-api/react-query'
import { useLogCashWebCdfEvent } from '@cash-web/shared-feature-analytics'
import { useExperiment } from '@cash-web/shared-feature-experiments'
import { getEnvironmentAtRunTime } from '@cash-web/shared-util-environment'
import { useExecuteOnce } from '@cash-web/shared-util-execute-once'
import { getAppDownloadUrlForPath, getCashmeDeepLink } from '@cash-web/shared-util-url'
import { isMobileDevice } from '@cash-web/shared-util-user-agent'
import { WebInteractClick } from '@squareup/customer-data-events-web'

export interface RedirectToMobileProps {
  deepLinkPath?: `/${string}`
  clientRouteUrl?: string
  children?: ReactNode
  bodyText?: string
}
// TODO - Move useQRImage to a shared library
const useQRImage = ({ path }: { path?: `/${string}` }) => {
  const theme = useTheme()

  const [loadedSrc, setLoadedSrc] = useState<string | null>(null)

  useEffect(() => {
    if (!path) return

    const src = new URL(
      '/qr' + path,
      getEnvironmentAtRunTime() === 'production' ? 'https://cash.app' : 'https://cashstaging.app'
    )

    src.searchParams.set('fg', theme.arcadeColors.semantic.textStandard)
    src.searchParams.set('bg', '#00000000' /* transparent */)
    src.searchParams.set('format', 'svg')
    src.searchParams.set('logoColor', theme.arcadeColors.semantic.iconBrand)

    const img = new Image()
    img.src = src.href
    img.onload = () => setLoadedSrc(src.href)
    img.onerror = () => setLoadedSrc(src.href)
  }, [path, theme])

  if (loadedSrc) {
    return { src: loadedSrc, isLoading: false } as const
  }

  return { src: null, isLoading: true } as const
}

const generateDefaultBodyText = (
  t: ReturnType<typeof useTranslation>['t'],
  shouldSendPush: boolean,
  hasMobileDeepLink: boolean
) => {
  if (shouldSendPush) {
    return t('Open Cash App on your phone to continue. We sent you a notification to get you started.', {
      context: 'redirect to mobile body text for push notification',
    })
  }
  if (hasMobileDeepLink) {
    return t('Open Cash App on your phone to continue.', {
      context: 'redirect to mobile body text for mobile web deep link',
    })
  }
  return t('Scan the QR code below to continue in the app.', {
    context: 'redirect to mobile body text for QR code',
  })
}

const MobileContent = ({
  deepLinkPath,
  handleDownloadCashApp,
  handleOpenInCashApp,
}: {
  deepLinkPath: `/${string}` | undefined
  handleDownloadCashApp: () => void
  handleOpenInCashApp: () => void
}) => {
  const { t } = useTranslation()
  return (
    <Stack space={spacing.s}>
      <ArcadeButton size="cta" prominence="prominent" onClick={handleDownloadCashApp}>
        {t('Download Cash App', { context: 'redirect to mobile button to download Cash App' })}
      </ArcadeButton>
      {deepLinkPath && (
        <ArcadeButton size="cta" prominence="standard" onClick={handleOpenInCashApp}>
          {t('Open in Cash App', { context: 'redirect to mobile button to open Cash App via deep link' })}
        </ArcadeButton>
      )}
    </Stack>
  )
}

export const RedirectToMobile = ({ deepLinkPath, clientRouteUrl, children, bodyText }: RedirectToMobileProps) => {
  const { t } = useTranslation()
  const [pushRequested, setPushRequested] = useState(false)
  const { mutate: triggerCampaign, isLoading } = useMutationTriggerCampaign({
    mutationOptions: {
      onError: () => {
        setPushRequested(false)
        setShowErrorToast(true)
      },
      onSuccess: () => {
        setPushRequested(false)
      },
    },
  })
  const logCdfEvent = useLogCashWebCdfEvent()
  const [showErrorToast, setShowErrorToast] = useState(false)

  const isMobile = isMobileDevice()

  const { src, isLoading: isImageLoading } = useQRImage({ path: isMobile ? undefined : deepLinkPath || '/download' })

  const isPushNotificationEnabled =
    useExperiment('cash-web-continue-in-mobile-push-notification')?.variant_value === 'treatment'

  const hasMobileDeepLink = Boolean(deepLinkPath && isMobile)
  const shouldSendPush = Boolean(isPushNotificationEnabled && clientRouteUrl && !hasMobileDeepLink)

  const headerText = t('View in the app', { context: 'redirect to mobile header text' })

  const displayBodyText = bodyText || generateDefaultBodyText(t, shouldSendPush, hasMobileDeepLink)

  const errorToastMessage = t('Something went wrong. Please try again.', {
    context: 'error toast when push notification fails',
  })

  const handleSendPushNotification = useCallback(
    (isInitialPush = false) => {
      if (!isInitialPush) {
        setPushRequested(true)
        logCdfEvent(
          WebInteractClick({
            componentName: 'RedirectToMobile',
            componentType: 'Send To Phone',
          })
        )
      }

      triggerCampaign({
        id: TriggerCampaignRequestId.WEB_UNSUPPORTED_CONTINUE_IN_MOBILE,
        client_route_url: clientRouteUrl,
      })
    },
    [clientRouteUrl, logCdfEvent, triggerCampaign]
  )

  useExecuteOnce(() => handleSendPushNotification(true), Boolean(shouldSendPush))

  const handleOpenInCashApp = () => {
    logCdfEvent(
      WebInteractClick({
        componentName: 'RedirectToMobile',
        componentType: 'Open in Cash App',
      })
    )
    if (deepLinkPath) {
      const url = getCashmeDeepLink(deepLinkPath)
      window.location.href = url
    }
  }

  const handleDownloadCashApp = () => {
    logCdfEvent(
      WebInteractClick({
        componentName: 'RedirectToMobile',
        componentType: 'Download Cash App',
      })
    )
    window.location.replace(getAppDownloadUrlForPath(deepLinkPath))
  }

  if (isImageLoading && !isMobile) {
    return null
  }

  return (
    <Flex flexDirection="column" gap={spacing.defaultContent}>
      {children}
      <Flex flexDirection="column" gap={spacing.s}>
        <DeviceMobileIcon size={32} />
        <Flex flexDirection="column" gap={spacing.s}>
          <Text typography="pageTitle" as="h2" color="textStandard">
            {headerText}
          </Text>
          <Text typography="body" color="textSubtle">
            {displayBodyText}
          </Text>
        </Flex>
      </Flex>
      {!isMobile && !isImageLoading && (
        <Flex justifyContent="center">
          <img
            src={src}
            alt={t('QR code to view flow in Cash App', { context: 'QR code alt text' })}
            width={200}
            height={200}
          />
        </Flex>
      )}
      {isMobile && (
        <MobileContent
          deepLinkPath={deepLinkPath}
          handleDownloadCashApp={handleDownloadCashApp}
          handleOpenInCashApp={handleOpenInCashApp}
        />
      )}
      {shouldSendPush && (
        <ArcadeButton
          size="cta"
          prominence="standard"
          onClick={() => handleSendPushNotification()}
          isLoading={pushRequested && isLoading}
        >
          {t('Send notification again', { context: 'Redirect to mobile button to send push notification' })}
        </ArcadeButton>
      )}
      {showErrorToast && (
        <Toast
          headline={errorToastMessage}
          dismissTimeout={3000}
          data-testid="redirect-to-mobile-error-toast"
          onClose={() => setShowErrorToast(false)}
        />
      )}
    </Flex>
  )
}
